import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Storage as FirebaseStorage,
  ref,
  UploadMetadata,
  UploadResult,
  uploadString,
} from '@angular/fire/storage';
import { parseGIF, decompressFrames } from 'gifuct-js';
import { keyBy } from 'lodash';
import MediaInfo from 'mediainfo.js';
import { lastValueFrom } from 'rxjs';
import { StreamData } from '../home/channels-view/schedule-event/schedule-event.component';

export type UploadStatus =
  | 'WAITING'
  | 'STARTED'
  | 'PAUSED'
  | 'FINISHED'
  | 'FAILED';

export type BulkImportItem = {
  name: string;
  error?: string | string[];
}

type BulkImportProps = {
  livestream: StreamData[]; ownerDocId: string; clubDocId: string; teamDocId: string 
}

export interface UploadFileProps {
  duration: number | undefined;
  width: number;
  height: number;
  format: string;
  frameCount: number;
  formatProfile: string;
  codecId: string;
  bitrate: number;
  method: string;
}

export class Upload {
  docId = '';
  file?: File;
  base64?: string;
  name = '';
  url = '';
  progress = 0;
  status!: UploadStatus;
  createdAt: Date = new Date();
  metadata!: UploadMetadata;

  constructor(data: string) {
    this.base64 = data;
  }
}

interface UploadPart {
  partNumber: number;
  signedUrl: string;
}

interface MultipartUploadResponse {
  parts: UploadPart[];
  uploadId: string;
  fileName: string;
}

interface S3UrlResponse {
  s3Url: string;
}

export interface UploadResultUrl {
  inputUrl: string;
}

export interface UploadError {
  error: string;
}

interface MediaInfoTrack {
  '@type': string;
  Duration?: string;
  Width?: string;
  Height?: string;
  Format?: string;
  Format_Profile?: string;
  CodecID?: string;
  OverallBitRate?: string;
  [key: string]: string | undefined;
}

interface MediaInfoResult {
  media?: {
    track?: MediaInfoTrack[];
  };
}

export interface MediaInfoInstance {
  analyzeData: (
    getSize: ()=> number,
    readChunk: (chunkSize: number, offset: number)=> Promise<Uint8Array>
  )=> Promise<MediaInfoResult>;
}

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  public static readonly SUPPORTED_VIDEO_FORMATS = [
    'video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska', 'video/x-flv', 'video/3gpp', 'video/3gpp2', 
    'video/mpeg', 'video/x-ms-asf', 'video/x-f4v', 'video/x-mxf', 'video/x-msvideo', 'video/x-ms-wmv', 'video/x-ms-asf',
    'video/x-ts', 'video/x-msmpeg', 'video/x-gif', 'video/x-m4v', 'video/mp2t', 'video/x-mts', 'video/x-m2ts',

    '.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.3gp', '.3g2', '.wmv',
    '.ts', '.mpg', '.mpeg', '.asf', '.f4v', '.mxf', '.gif', '.m3u8'
  ];

  public static readonly EXTENSION_TO_MIME: Record<string, string> = {
    '.f4v': 'video/x-f4v',
    '.flv': 'video/x-flv',
    '.mxf': 'video/x-mxf',
    '.mp4': 'video/mp4',
    '.webm': 'video/webm',
    '.mov': 'video/quicktime',
    '.avi': 'video/x-msvideo',
    '.mkv': 'video/x-matroska',
    '.wmv': 'video/x-ms-wmv',
    '.3gp': 'video/3gpp',
    '.3g2': 'video/3gpp2',
    '.mpg': 'video/mpeg',
    '.mpeg': 'video/mpeg',
    '.asf': 'video/x-ms-asf',
    '.ts': 'video/mp2t',
    '.m4v': 'video/x-m4v',
    '.gif': 'video/x-gif'
  };
  private readonly basePath = '/uploads';
  private uploadTask!: UploadResult;


  constructor(
    private readonly storage: FirebaseStorage,
    private readonly http: HttpClient,
    private readonly functions: Functions
  ) {}

  async initializeMediaInfo() {
    const mediaInfo = await MediaInfo({
        locateFile: () => '/assets/mediainfo/MediaInfoModule.wasm',
    });
    return mediaInfo;
  }

  isValidVideoFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    if (fileType && UploadService.SUPPORTED_VIDEO_FORMATS.includes(fileType)) {
      return true;
    }
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    return UploadService.SUPPORTED_VIDEO_FORMATS.includes(extension);
  }

  isGifFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    return fileType === 'image/gif' || fileType === 'video/x-gif' || fileName.endsWith('.gif');
  }

  async extractGifMetadata(file: File): Promise<Partial<UploadFileProps>> {
    const buffer = await file.arrayBuffer();
    const gif = parseGIF(buffer);
    const frames = decompressFrames(gif, true);
  
    const duration = frames.reduce((acc, frame) => acc + (frame.delay || 10), 0);
    return {
      duration: duration * 10 / 1000,
      width: gif.lsd.width,
      height: gif.lsd.height,
      frameCount: frames.length,
      method: 'GIFUCT_JS'
    };
  }

  extractMetadataWithHTML5(file: File): Promise<Partial<UploadFileProps>> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const timeout = setTimeout(() => {
        globalThis.URL.revokeObjectURL(video.src);
        reject(new Error('HTML5 metadata extraction timeout'));
      }, 5000);
      
      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        globalThis.URL.revokeObjectURL(video.src);
        
        if (video.duration && video.duration > 0) {
          resolve({
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight,
            method: 'HTML5'
          });
        } else {
          reject(new Error('Invalid duration from HTML5'));
        }
      };
      
      video.onerror = () => {
        clearTimeout(timeout);
        globalThis.URL.revokeObjectURL(video.src);
        reject(new Error('HTML5 video loading failed'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  async extractMetadataWithoutMediaInfo(file: File): Promise<Partial<UploadFileProps> | undefined> {
  if (this.isGifFile(file)) {
    try {
      return await this.extractGifMetadata(file);
    } catch (error) {
      console.warn('Failed to extract GIF metadata using gifuct-js:', error);
    }
  }
  
  try {
    const html5Metadata = await this.extractMetadataWithHTML5(file);
    if (html5Metadata && html5Metadata.duration! > 0) {
      return html5Metadata;
    }
  } catch (error) {
    console.log('HTML5 method failed, trying MediaInfo method', error);
  }
  return undefined;
}

async extractMetadataWithMediaInfo(file: File, mediaInfo: MediaInfoInstance): Promise<Partial<UploadFileProps>> {
    if (!mediaInfo) {
      throw new Error('MediaInfo not initialized');
    }
    
    const fileSize = file.size;
    const getSize = () => fileSize;
    
    const readChunk = (chunkSize: number, offset: number): Promise<Uint8Array> => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result instanceof ArrayBuffer) {
            resolve(new Uint8Array(event.target.result));
          } else {
            reject(new Error('Failed to read file chunk'));
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file.slice(offset, offset + chunkSize));
      });
    
    const result = await mediaInfo.analyzeData(getSize, readChunk);
    
    const videoTrack = result.media?.track?.find((track: MediaInfoTrack) => track['@type'] === 'Video');
    const generalTrack = result.media?.track?.find((track: MediaInfoTrack) => track['@type'] === 'General');
    
    if (!generalTrack?.Duration) {
      throw new Error('Unable to extract duration from MediaInfo');
    }
    
    return {
      duration: Number.parseFloat(generalTrack.Duration),
      width: Number.parseInt(videoTrack?.Width || '0'),
      height: Number.parseInt(videoTrack?.Height || '0'),
      format: generalTrack.Format,
      formatProfile: generalTrack.Format_Profile,
      codecId: generalTrack.CodecID,
      bitrate: Number.parseInt(generalTrack.OverallBitRate || '0'),
      method: 'MediaInfo'
    };
  }

  async extractVideoMetadata(file: File, mediaInfo: MediaInfoInstance): Promise<Partial<UploadFileProps>> {
  const metadataWithoutMediaInfo = await this.extractMetadataWithoutMediaInfo(file);
  
  if (metadataWithoutMediaInfo) {
    return metadataWithoutMediaInfo;
  }
  
  try {
    return await this.extractMetadataWithMediaInfo(file, mediaInfo);
  } catch (error) {
    console.error('All metadata extraction methods failed:', error);
    throw error;
  }
}

  determineCorrectMimeType(file: File, metadata: Partial<UploadFileProps>): string {
    if (file.type && file.type.trim() !== '') {
      return file.type;
    }

    const mimeFromMetadata = this.getMimeTypeFromMetadata(metadata);
    if (mimeFromMetadata) {
      return mimeFromMetadata;
    }

    const mimeFromExtension = this.getMimeTypeFromExtension(file.name);
    if (mimeFromExtension) {
      return mimeFromExtension;
    }

    return 'video/mp4';
  }

  private getMimeTypeFromMetadata(metadata: Partial<UploadFileProps>): string | null {
    if (!metadata.format) {
      return null;
    }

    const format = metadata.format;
    const formatProfile = metadata.formatProfile;
    const codecId = metadata.codecId;

    if (format === 'MPEG-4') {
      if (formatProfile === 'Adobe Flash' || 
          (codecId?.trim().toLowerCase().startsWith('f4v'))) {
        return 'video/x-f4v';
      }
      return 'video/mp4';
    }
    if (format === 'Flash Video' || format === 'FLV') {
      return 'video/x-flv';
    }
    
    if (format === 'MXF') {
      return 'video/x-mxf';
    }

    const formatMimeMap: Record<string, string> = {
      QuickTime: 'video/quicktime',
      Matroska: 'video/x-matroska',
      AVI: 'video/x-msvideo',
      'Windows Media': 'video/x-ms-wmv',
      'MPEG Video': 'video/mpeg',
      WebM: 'video/webm'
    };

    return formatMimeMap[format] || null;
  }

  private getMimeTypeFromExtension(fileName: string): string | null {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return UploadService.EXTENSION_TO_MIME[extension] || null;
  }

  createFileWithCorrectMimeType(originalFile: File, correctMimeType: string): File {
    const fileOptions = {
      type: correctMimeType,
      lastModified: originalFile.lastModified
    };
    
    return new File([originalFile], originalFile.name, fileOptions);
  }

  async pushUpload(upload: Upload, path: string, base64 = true) {
    const destination = [
      this.basePath,
      path,
      upload.name.toLowerCase().split(' ').join('-'),
    ].join('/');
    const storageRef = ref(this.storage, destination);
    
    this.uploadTask = await uploadString(
      storageRef,
      upload.base64?.split(',')[1] || '',
      'base64',
      upload.metadata
    );

    return this.uploadTask;
  }

  async uploadAd(adDocId: string, file: any, channelDocId: string) {
    const promises: Promise<[videoAccessUrl: string]>[] = file.map(async (element: any) => {
      const { signedUrl, videoAccessUrl } = (await httpsCallable(this.functions, 'ads-uploadUrl')({
          requestType: 'VIDEOS_PRE_SIGNED_URL',
          contentType: element.type,
          channelDocId
        })).data as any;

      await lastValueFrom(
        this.http.put(signedUrl, element, {
          headers: { 'Content-Type': element.type },
        })
      );
      return [videoAccessUrl];
    });
    return await Promise.all(promises);
  }

  async uploadImage(adDocId: string, file: any, country: string, channelId: string) {
    const promises=file.map(async (element: any) => {
      const response = (await httpsCallable(this.functions, 'ads-uploadUrl')({
          fileName: `public/channels/${channelId}/${adDocId}/${element.name}`,
          contentType: element.type,
          channelDocId: channelId,
          requestType: 'IMAGES_PRE_SIGNED_URL'
        })).data as any;

      await lastValueFrom(this.http.put(response.signedUrl, element, {
        headers: { 'Content-Type': element.type },
      }));

      return response.publicUrl;
    });
    const publicUrl= await Promise.all(promises);
    return publicUrl;
  }

  async pushNotificationUpload(upload: Upload, path: string, base64 = true) {
    const destination = [path, upload.name].join('/');
    const storageRef = ref(this.storage, destination);

    
    this.uploadTask = await uploadString(
        storageRef,
      upload.base64?.split(',')[1] || '',
      'base64',
      upload.metadata
    );

    return this.uploadTask;
  }

  async uploadStreams(
    streamList: StreamData[],
    ownerDocId: string,
    clubDocId: string,
    teamDocId: string
  ): Promise<BulkImportItem[]> {
    const bulkImport = httpsCallable<BulkImportProps, BulkImportItem[]>(
      this.functions, 
      'livestreams-bulkImport'
    );
    
    const response = await bulkImport({
      livestream: streamList,
      ownerDocId: ownerDocId,
      clubDocId: clubDocId,
      teamDocId: teamDocId,
    });

    return response.data;
  }

  async multipartUpload(
    channelDocId: string,
    file: File,
    country: string,
    videoName: string
  ): Promise<UploadResultUrl | UploadError> {
    const FILE_CHUNK_SIZE = 5 * 1024 * 1024 * 1024;
    const fileSize = file.size;
    const PARTS = Math.floor(fileSize / FILE_CHUNK_SIZE) + 1;

    try {
      const uploadUrl = httpsCallable(this.functions, 'videos-uploadUrl');
      const response = (
        await uploadUrl({
          channelDocId: channelDocId,
          contentType: file.type,
          parts: PARTS.toString(),
          requestType: 'START_UPLOAD',
        })
      ).data as MultipartUploadResponse;

      const firstPresignedUrl = response.parts[0].signedUrl;
      const signedUrls = keyBy(response.parts, 'PartNumber');
      let start, end, blob;

      const uploadPartsArray: { ETag: string; PartNumber: number | undefined }[] =
        [];
      const orderData: { presignedUrl: string; index: number }[] = [];
      const promises=[];

      for (let index = 1; index < PARTS + 1; index++) {
        start = (index - 1) * FILE_CHUNK_SIZE;
        end = index * FILE_CHUNK_SIZE;
        blob = index < PARTS ? file.slice(start, end) : file.slice(start);

        orderData.push({
          presignedUrl: signedUrls[index].signedUrl.toString(),
          index: index,
        });

        const req = new HttpRequest(
          'PUT',
          signedUrls[index].signedUrl.toString(),
          blob
        );

        const putResponse = lastValueFrom(this.http.request(req));
        promises.push(putResponse);
      }

      const uploadedparts = await Promise.all(promises);
      for (const [index, parts] of uploadedparts.entries()) {
        const currentPresigned = orderData.find(
          (item) => item.presignedUrl === (parts as any).url
        );
        const etag = (parts as any).headers.get('ETag') || '';
        uploadPartsArray.push({
          ETag: etag.replace(/[|&;$%@"<>()+,]/g, ''),
          PartNumber: currentPresigned ? currentPresigned.index : index + 1,
        });
      }

      if (uploadPartsArray.length === PARTS) {
        const completeUpload = httpsCallable(
          this.functions,
          'videos-uploadUrl'
        );
        const finalResponse = (
          await completeUpload({
            requestType: 'COMPLETE_UPLOAD',
            fileName: response.fileName,
            contentType: file.type,
            uploadId: response.uploadId,
            parts: uploadPartsArray,
            url: firstPresignedUrl
          })
        ).data as S3UrlResponse;

        const result = {
          inputUrl: finalResponse.s3Url,
        };

        return result;
      } else {
        return { error: 'The Upload failed' };
      }
    } catch (error) {
      return { error: error as string };
    }
  }
}
